import {
  CheckCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import { SetStateType } from 'commonTypes';
import { MENU_PANEL_URL } from 'configure';
import { useDebounce } from 'helpers/hooks';
import { phoneMask } from 'helpers/validation';
import { useAppDispatch } from 'hooks/store';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tooltip } from 'react-tooltip';
import { checkAvailabilityLink } from 'store/qrcode/action';

import { RegisterPhoneCodeSelect } from './RegisterPhoneCodeSelect';

interface RegisterPhoneInputProps {
  phoneCode:
    | {
        code: string;
        name: string;
      }
    | undefined;
  setPhoneCode: SetStateType<
    | {
        code: string;
        name: string;
      }
    | undefined
  >;
  phoneNumber: string;
  setPhoneNumber: SetStateType<string>;

  link: string;
  setLink: SetStateType<string>;
  availableLink: boolean | null;
  setAvailableLink: SetStateType<boolean | null>;
}

export const RegisterPhoneInput = ({
  phoneCode,
  setPhoneCode,
  phoneNumber,
  setPhoneNumber,

  link,
  setLink,
  availableLink,
  setAvailableLink,
}: RegisterPhoneInputProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const debouncedSearchTerm = useDebounce(link, 300);

  useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(
        checkAvailabilityLink({
          link: debouncedSearchTerm,
          setAvailableLink,
        }),
      );
    } else {
      setAvailableLink(null);
    }
  }, [debouncedSearchTerm]);

  return (
    <div className="flex w-full items-start flex-wrap sm:flex-nowrap justify-between space-y-6 sm:space-x-8 sm:space-y-0">
      <div className="w-full sm:w-1/2">
        <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700">
          <FormattedMessage id="phoneNumber" />
        </label>
        <div className="relative mt-1 rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 flex items-center">
            <label htmlFor="country" className="sr-only">
              <FormattedMessage id="country" />
            </label>
            <RegisterPhoneCodeSelect phoneCode={phoneCode} setPhoneCode={setPhoneCode} />
          </div>
          <input
            type="text"
            value={phoneNumber}
            //   onChange={(e) => setPhoneNumber(e.target.value)}
            onInput={(e) => {
              phoneMask(e, setPhoneNumber);
            }}
            required
            name="phone-number"
            id="phone-number"
            autoComplete="tel-national"
            className="block w-full rounded-md border-gray-300 pl-28 md:focus:border-indigo-500 md:focus:ring-indigo-500 sm:text-sm"
            placeholder={intl.formatMessage({ id: 'phoneNumber' })}
          />
        </div>
      </div>
      <div className="w-full sm:w-1/2">
        <label htmlFor="link" className="text-sm font-medium text-gray-700 flex">
          <div>
            <FormattedMessage id="link" />
          </div>
          <InformationCircleIcon
            data-tooltip-id="domain"
            className="h-5 w-5 text-gray-500 ml-2 cursor-pointer hover:text-gray-400"
            aria-hidden="true"
          />
          <Tooltip place="top" id="domain">
            <div className="max-w-[300px]">
              <FormattedMessage id="textLink" />
            </div>
          </Tooltip>
        </label>
        <div className="mt-1 flex items-center relative">
          <input
            id="link"
            name="dm-link"
            value={link}
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              setLink(target.value.toLowerCase().replace(/[^a-z0-9._-]/gi, ''));
            }}
            type="text"
            required
            placeholder="demo-restaurant"
            className={`block w-full rounded-md pr-10 ${
              availableLink === null
                ? 'text-gray-900 border-gray-300 placeholder-gray-300 focus:border-primary focus:ring-primary'
                : availableLink
                  ? 'text-green-900 border-green-300 placeholder-green-300 focus:border-green-500 focus:ring-green-500'
                  : 'text-red-900 border-red-300 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
            }  focus:outline-none  sm:text-sm`}
          />
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            {availableLink === null ? (
              <></>
            ) : availableLink ? (
              <CheckCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
            )}
          </div>
        </div>
        {(availableLink || availableLink === null) && (
          <p className="mt-2 text-xs text-primary truncate">
            {MENU_PANEL_URL}/{link ? link : '[your domain name]'}
          </p>
        )}
        {!availableLink && availableLink !== null && (
          <p className="mt-2 text-xs text-red-500 truncate">
            <FormattedMessage id="accountLinkAlreadyExist" />
          </p>
        )}
      </div>
    </div>
  );
};
